import React, { useState, useMemo, useEffect } from 'react';
import { HIDDEN_FILE_INPUT_CLASS, JSON_ACCEPT } from '../utils/filePicker';
import {
  Settings,
  Save,
  Download,
  Upload,
  Eye,
  Edit3,
  Plus,
  Video,
  Image,
  Music,
  CreditCard,
  FileText,
  Palette,
  Layout,
  Type,
  Clock,
  ChevronDown,
  ChevronRight,
  X,
  Sliders,
  Grid,
  Layers,
  Monitor,
  Code,
  BookOpen,
  BarChart3,
  Table,
  ListOrdered,
  AlertCircle,
  CheckSquare,
  AlertTriangle,
  Columns,
  ImagePlus,
  LayoutGrid,
  Heading,
  TextIcon,
  StickyNote,
  Check,
} from 'lucide-react';

// Section collapse/expand helper
const CollapsibleSection = ({ title, subtitle, icon: Icon, defaultOpen = false, badge, children }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="rounded-xl border border-gray-700 bg-gray-800 shadow-sm overflow-hidden">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between gap-3 px-5 py-4 hover:bg-gray-700 transition-colors"
      >
        <div className="flex items-center gap-3">
          {Icon && <Icon size={18} className="text-gray-400" />}
          <div className="text-left">
            <h3 className="text-sm font-semibold text-gray-100">{title}</h3>
            {subtitle && <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {badge && (
            <span className="text-[10px] uppercase tracking-wide font-semibold px-2 py-0.5 rounded-full bg-blue-900/30 text-blue-400">
              {badge}
            </span>
          )}
          {isOpen ? <ChevronDown size={16} className="text-gray-400" /> : <ChevronRight size={16} className="text-gray-400" />}
        </div>
      </button>
      {isOpen && (
        <div className="px-5 pb-5 pt-1 border-t border-gray-700">
          {children}
        </div>
      )}
    </div>
  );
};

const ContentBlockButton = ({ label, icon: Icon, color, onClick, isSelected }) => (
  <button
    type="button"
    onClick={onClick}
    className={`flex flex-col items-center justify-center gap-1.5 p-3 rounded-xl border-2 transition-all hover:shadow-md hover:scale-[1.02] ${
      isSelected
        ? 'border-blue-500 bg-blue-900/40 text-blue-300 ring-2 ring-blue-500/30 shadow-lg shadow-blue-500/20 border-solid'
        : `border-dashed ${color}`
    }`}
  >
    <Icon size={20} />
    <span className="text-[11px] font-semibold">{label}</span>
  </button>
);

const PresetLayoutCard = ({ layout, onClick }) => (
  <button
    type="button"
    onClick={() => onClick(layout)}
    className="group relative p-3 rounded-xl border border-gray-600 bg-gray-700 hover:border-blue-500/50 hover:bg-gray-600 transition-all hover:shadow-md"
  >
    <div className="flex flex-col items-center gap-2">
      <div className="w-full aspect-video bg-gray-800 rounded-lg border border-gray-600 flex items-center justify-center overflow-hidden">
        {layout.preview}
      </div>
      <span className="text-[11px] font-semibold text-gray-300 group-hover:text-blue-400">{layout.label}</span>
    </div>
  </button>
);

// Layout preview thumbnails
const LayoutThumbnail = ({ type }) => {
  const layouts = {
    'single-column': (
      <div className="w-full h-full p-1.5 flex flex-col gap-1">
        <div className="bg-gray-500 rounded h-2 w-3/4" />
        <div className="bg-gray-600 rounded flex-1" />
      </div>
    ),
    'two-column': (
      <div className="w-full h-full p-1.5 flex gap-1">
        <div className="bg-gray-600 rounded flex-1" />
        <div className="bg-gray-600 rounded flex-1" />
      </div>
    ),
    'three-column': (
      <div className="w-full h-full p-1.5 flex gap-1">
        <div className="bg-gray-600 rounded flex-1" />
        <div className="bg-gray-600 rounded flex-1" />
        <div className="bg-gray-600 rounded flex-1" />
      </div>
    ),
    'image-text': (
      <div className="w-full h-full p-1.5 flex gap-1">
        <div className="bg-blue-100 rounded flex-1 flex items-center justify-center">
          <Image size={10} className="text-blue-300" />
        </div>
        <div className="flex-1 flex flex-col gap-0.5 justify-center">
          <div className="bg-gray-500 rounded h-1 w-full" />
          <div className="bg-gray-600 rounded h-1 w-3/4" />
          <div className="bg-gray-600 rounded h-1 w-1/2" />
        </div>
      </div>
    ),
    'cards-grid': (
      <div className="w-full h-full p-1.5 grid grid-cols-2 gap-1">
        <div className="bg-gray-600 rounded border border-gray-500" />
        <div className="bg-gray-600 rounded border border-gray-500" />
        <div className="bg-gray-600 rounded border border-gray-500" />
        <div className="bg-gray-600 rounded border border-gray-500" />
      </div>
    ),
    'hero-section': (
      <div className="w-full h-full p-1.5 flex flex-col gap-1">
        <div className="bg-blue-100 rounded flex-1 flex items-center justify-center">
          <Type size={10} className="text-blue-300" />
        </div>
        <div className="bg-gray-600 rounded h-2" />
      </div>
    ),
  };
  return layouts[type] || layouts['single-column'];
};

const PRESET_LAYOUTS_DATA = [
  { id: 'single-column', label: 'Single Column', type: 'single-column', presetKey: 'single-column' },
  { id: 'two-columns', label: 'Two Columns', type: 'two-column', presetKey: 'two-columns' },
  { id: 'three-columns', label: 'Three Columns', type: 'three-column', presetKey: 'three-columns' },
  { id: 'image-and-text', label: 'Image + Text', type: 'image-text', presetKey: 'image-and-text' },
  { id: 'cards-grid', label: 'Card Grid', type: 'cards-grid', presetKey: 'cards-grid' },
  { id: 'hero-section', label: 'Hero Section', type: 'hero-section', presetKey: 'hero-section' },
  { id: 'title-slide', label: 'Title Slide', type: 'single-column', presetKey: 'title-slide' },
  { id: 'title-and-content', label: 'Title + Content', type: 'single-column', presetKey: 'title-and-content' },
  { id: 'title-text-image', label: 'Title / Text / Image', type: 'image-text', presetKey: 'title-text-image' },
  { id: 'comparison', label: 'Comparison', type: 'two-column', presetKey: 'comparison' },
  { id: 'picture-with-caption', label: 'Picture + Caption', type: 'single-column', presetKey: 'picture-with-caption' },
  { id: 'title-text-video', label: 'Title / Text / Video', type: 'image-text', presetKey: 'title-text-video' },
];

const TemplateControlsPage = ({
  // Session details
  week,
  onWeekChange,
  date,
  onDateChange,
  instructorName,
  instructorEmail,
  onHeaderDataChange,
  headerData,
  // Layout
  bodyLayout,
  onBodyLayoutChange,
  bodyBackground,
  slideBackgrounds,
  onBodyBackgroundChange,
  onApplyBackgroundSelection,
  // Slide settings
  slideHeaderMode,
  slideHeaderColor,
  slideHeaderColors,
  onSlideHeaderModeChange,
  onSlideHeaderColorChange,
  onSlideHeaderSectionColorChange,
  slideGlobalCss,
  onSlideGlobalCssChange,
  // Title cards
  titleCardSettings,
  onTitleCardUpdate,
  onTitleCardImageChange,
  endTitleCardSettings,
  onEndTitleCardUpdate,
  onEndTitleCardImageChange,
  // Slide deck
  slideDeckName,
  onSlideDeckNameChange,
  // Export
  exportFileName,
  onExportFileNameChange,
  htmlExportTitle,
  onHtmlExportTitleChange,
  exportVersion,
  // Mode & branding
  isEditMode,
  onToggleEditMode,
  onOpenLogoSettings,
  // Save/Export actions
  onSave,
  onLoad,
  onExportPDF,
  onExportPPTX,
  onExportHTML,
  onExportOptimizedHTML,
  onExportSlimHTML,
  onExportPathHTML,
  onExportSlimJson,
  onExportStableJson,
  onExportLlmTemplate,
  onDownloadScopedCssExample,
  showAdvancedExports,
  setShowAdvancedExports,
  // Content
  sections,
  defaultSection,
  onDefaultSectionChange,
  onAddSection,
  onAddContent,
  // Status
  hasUnsavedChanges,
  lastSaved,
  // Helpers
  sectionNumberMap,
  BACKGROUND_PRESETS,
  getBackgroundStyles,
  getSectionHeaderLabel,
  // Preset layout actions
  onApplyPresetLayout,
  onApplyColumnLayout,
  currentSlideInfo,
  // Slide targeting
  sectionSlidesMap,
  slides,
  currentSlideIndex,
  lightMode = false,
}) => {
  const [activeTab, setActiveTab] = useState('settings');
  const [lastAppliedLayout, setLastAppliedLayout] = useState(null);
  const [showColumnConfirm, setShowColumnConfirm] = useState(null);
  const [selectedBlock, setSelectedBlock] = useState(null);
  const [targetSlideId, setTargetSlideId] = useState('');
  const [backgroundTarget, setBackgroundTarget] = useState('all');
  const [pendingBackground, setPendingBackground] = useState(bodyBackground || 'white');
  const [backgroundFeedback, setBackgroundFeedback] = useState('');
  const [customColor, setCustomColor] = useState('#ffffff');
  const [customGradientFrom, setCustomGradientFrom] = useState('#ffffff');
  const [customGradientTo, setCustomGradientTo] = useState('#e0f2fe');
  const [customGradientAngle, setCustomGradientAngle] = useState(135);

  useEffect(() => {
    if (backgroundTarget === 'all') {
      setPendingBackground(bodyBackground || 'white');
      return;
    }
    const targeted = slideBackgrounds?.[backgroundTarget];
    setPendingBackground(targeted || bodyBackground || 'white');
  }, [backgroundTarget, bodyBackground, slideBackgrounds]);

  // Get slides for the currently selected section
  const currentSectionSlides = useMemo(() => {
    if (!sectionSlidesMap || !defaultSection) return [];
    return (sectionSlidesMap[defaultSection] || []).filter(s => !s.isSectionTitleCard);
  }, [sectionSlidesMap, defaultSection]);

  const handleBlockSelect = (blockType, blockLabel) => {
    setSelectedBlock(selectedBlock?.type === blockType ? null : { type: blockType, label: blockLabel });
  };

  const handleApplyBlock = () => {
    if (!selectedBlock || !defaultSection) return;
    onAddContent(defaultSection, selectedBlock.type, { forceModal: true, source: 'toolbar' });
    setSelectedBlock(null);
  };

  const handleApplyPreset = (presetKey, layoutLabel) => {
    if (onApplyPresetLayout) {
      onApplyPresetLayout(presetKey);
      setLastAppliedLayout(layoutLabel);
      setTimeout(() => setLastAppliedLayout(null), 3000);
    }
  };

  const handleCustomBackgroundUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      setPendingBackground({
        type: 'image',
        value: reader.result,
        name: file.name,
      });
    };
    reader.readAsDataURL(file);
  };

  const applySelectedBackground = () => {
    const target = backgroundTarget || 'all';
    if (target === 'all') {
      onBodyBackgroundChange?.(pendingBackground);
    }
    onApplyBackgroundSelection?.({ target, background: pendingBackground });

    const targetLabel = target === 'all'
      ? 'all slides'
      : (slides?.find((slide) => slide.slideId === target)?.label || 'selected slide');
    setBackgroundFeedback(`Applied background to ${targetLabel}.`);
    window.setTimeout(() => setBackgroundFeedback(''), 2200);
  };

  const tabs = [
    { id: 'settings', label: 'Settings', icon: Settings },
    { id: 'design', label: 'Design', icon: Palette },
    { id: 'export', label: 'Export', icon: Download },
  ];

  return (
    <div className={`min-h-screen ${lightMode ? "bg-white" : "bg-gradient-to-br from-gray-900 to-gray-950"}`}>
      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-sky-500 to-blue-600 flex items-center justify-center shadow-lg shadow-blue-500/20">
              <Settings size={20} className="text-white" />
            </div>
            <div>
              <h1 className={`text-2xl font-bold ${lightMode ? "text-gray-900" : "text-white"}`}>Template Controls</h1>
              <p className={`text-sm ${lightMode ? "text-gray-600" : "text-gray-400"}`}>Configure your slide template settings, design, and export options.</p>
            </div>
          </div>
          {hasUnsavedChanges && (
            <div className="mt-3 inline-flex items-center gap-2 bg-amber-900/30 border border-amber-500/30 rounded-lg px-3 py-2">
              <Clock size={14} className="text-amber-400" />
              <span className="text-sm text-amber-400 font-medium">Unsaved changes</span>
              <span className="text-xs text-amber-500">Last saved: {lastSaved?.toLocaleTimeString()}</span>
            </div>
          )}
        </div>

        {/* Tab Navigation */}
        <div className="flex items-center gap-1 mb-6 bg-gray-800 rounded-xl p-1 border border-gray-700 shadow-sm overflow-x-auto">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'text-gray-400 hover:bg-gray-700 hover:text-gray-200'
                }`}
              >
                <Icon size={16} />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        <div className="space-y-4">
          {/* =========== SETTINGS TAB =========== */}
          {activeTab === 'settings' && (
            <>
              <CollapsibleSection title="Session Details" subtitle="Week, date, and instructor information" icon={BookOpen} defaultOpen>
                <div className="space-y-4 mt-3">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-300 mb-2">Week</label>
                      <select
                        value={week}
                        onChange={(e) => onWeekChange(e.target.value)}
                        className="w-full p-3 border border-gray-600 bg-gray-700 text-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        {Array.from({ length: 15 }, (_, i) => (
                          <option key={i + 1} value={i + 1}>Week {i + 1}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-300 mb-2">Date</label>
                      <input
                        type="date"
                        value={date}
                        onChange={(e) => onDateChange(e.target.value)}
                        className="w-full p-3 border border-gray-600 bg-gray-700 text-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-300 mb-2">Instructor Name</label>
                      <input
                        type="text"
                        value={instructorName}
                        onChange={(e) => onHeaderDataChange('instructorName', e.target.value)}
                        className="w-full p-3 border border-gray-600 bg-gray-700 text-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-300 mb-2">Instructor Email</label>
                      <input
                        type="email"
                        value={instructorEmail}
                        onChange={(e) => onHeaderDataChange('instructorEmail', e.target.value)}
                        className="w-full p-3 border border-gray-600 bg-gray-700 text-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-300 mb-2">Course Title</label>
                    <input
                      type="text"
                      value={headerData?.courseTopic || ''}
                      onChange={(e) => onHeaderDataChange('courseTopic', e.target.value)}
                      placeholder="Enter course title"
                      className="w-full p-3 border border-gray-600 bg-gray-700 text-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </CollapsibleSection>



              <CollapsibleSection title="Layout & Slides" subtitle="Choose presentation mode and slide deck settings" icon={Layout} defaultOpen>
                <div className="space-y-4 mt-3">
                  <div>
                    <label className="block text-sm font-semibold text-gray-300 mb-2">Body Layout</label>
                    <select
                      value={bodyLayout}
                      onChange={(e) => onBodyLayoutChange(e.target.value)}
                      className="w-full p-3 border border-gray-600 bg-gray-700 text-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="accordion">Accordion</option>
                      <option value="slides">Slides</option>
                    </select>
                    <p className="text-xs text-gray-400 mt-2">Switch between the traditional accordion view and the slide deck experience.</p>
                  </div>

                  {bodyLayout === 'slides' && (
                    <>
                      <div>
                        <label className="block text-sm font-semibold text-gray-300 mb-2">Slide Deck Name</label>
                        <input
                          type="text"
                          value={slideDeckName}
                          onChange={(e) => onSlideDeckNameChange(e.target.value)}
                          className="w-full p-3 border border-gray-600 bg-gray-700 text-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="e.g., Capstone Production Schedule"
                        />
                        <p className="text-xs text-gray-400 mt-2">Appears on every slide and can be edited directly on the slide itself.</p>
                      </div>

                      <div className="rounded-lg border border-gray-700 bg-gray-800 p-4 space-y-3">
                        <div className="flex items-center justify-between">
                          <label className="text-sm font-semibold text-gray-300">Slide Header Color</label>
                          <span className="text-[11px] uppercase tracking-wide text-gray-500">Design</span>
                        </div>
                        <select
                          value={slideHeaderMode}
                          onChange={(e) => onSlideHeaderModeChange(e.target.value)}
                          className="w-full p-3 border border-gray-600 bg-gray-700 text-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          <option value="single">Use one color for all slides</option>
                          <option value="per-section">Different colors per section</option>
                        </select>
                        <div className="flex items-center gap-3">
                          <input
                            type="color"
                            value={slideHeaderColor}
                            onChange={(e) => onSlideHeaderColorChange(e.target.value)}
                            className="h-10 w-14 border border-gray-600 rounded cursor-pointer"
                          />
                          <span className="text-sm text-gray-400">Default header color</span>
                        </div>
                        {slideHeaderMode === 'per-section' && sections && (
                          <div className="grid grid-cols-1 gap-3 mt-2">
                            {sections.map((section) => (
                              <div key={section.id} className="flex items-center justify-between gap-3 bg-gray-700 p-2.5 rounded-lg border border-gray-600">
                                <span className="text-sm text-gray-300">{section.title}</span>
                                <input
                                  type="color"
                                  value={slideHeaderColors?.[section.id] || slideHeaderColor}
                                  onChange={(e) => onSlideHeaderSectionColorChange(section.id, e.target.value)}
                                  className="h-8 w-12 border border-gray-600 rounded cursor-pointer"
                                />
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </>
                  )}

                </div>
              </CollapsibleSection>

              <CollapsibleSection title="Mode & Branding" subtitle="Toggle editing mode and manage school logo" icon={Monitor}>
                <div className="space-y-3 mt-3">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <button
                      onClick={onToggleEditMode}
                      className={`w-full p-3 rounded-xl font-semibold transition-all flex items-center justify-center gap-3 ${
                        isEditMode
                          ? 'bg-emerald-500 hover:bg-emerald-600 text-white shadow-md'
                          : 'bg-gray-600 hover:bg-gray-500 text-white shadow-md'
                      }`}
                    >
                      {isEditMode ? <Eye size={18} /> : <Edit3 size={18} />}
                      {isEditMode ? 'Switch to Preview' : 'Switch to Edit'}
                    </button>
                    <button
                      onClick={onOpenLogoSettings}
                      className="w-full p-3 bg-gray-600 hover:bg-gray-500 text-white rounded-xl font-medium flex items-center justify-center gap-2 transition-colors shadow-sm"
                    >
                      <Settings size={16} />
                      School Logo Settings
                    </button>
                  </div>
                </div>
              </CollapsibleSection>

              {/* Title Cards */}
              <CollapsibleSection title="Title Card" subtitle="Configure the opening title slide" icon={CreditCard}>
                <div className="space-y-3 mt-3">
                  <label className="flex items-center gap-2 text-sm text-gray-300">
                    <input
                      type="checkbox"
                      checked={titleCardSettings?.enabled}
                      onChange={(e) => onTitleCardUpdate({ enabled: e.target.checked })}
                    />
                    Add a title card slide at the start
                  </label>
                  <input
                    type="text"
                    value={titleCardSettings?.title || ''}
                    onChange={(e) => onTitleCardUpdate({ title: e.target.value })}
                    className="w-full p-3 border border-gray-600 bg-gray-700 text-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Title card title"
                  />
                  <input
                    type="text"
                    value={titleCardSettings?.subtitle || ''}
                    onChange={(e) => onTitleCardUpdate({ subtitle: e.target.value })}
                    className="w-full p-3 border border-gray-600 bg-gray-700 text-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Title card subtitle"
                  />
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      value={titleCardSettings?.backgroundColor || '#1e3a5f'}
                      onChange={(e) => onTitleCardUpdate({ backgroundColor: e.target.value })}
                      className="h-10 w-14 border border-gray-600 rounded cursor-pointer"
                    />
                    <span className="text-sm text-gray-400">Background color</span>
                  </div>
                  <label className="flex items-center gap-2 text-sm text-gray-300">
                    <input
                      type="checkbox"
                      checked={titleCardSettings?.showCourseInfo !== false}
                      onChange={(e) => onTitleCardUpdate({ showCourseInfo: e.target.checked })}
                    />
                    Show course/instructor info
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-300">
                    <input
                      type="checkbox"
                      checked={titleCardSettings?.showLogoOnTitle !== false}
                      onChange={(e) => onTitleCardUpdate({ showLogoOnTitle: e.target.checked })}
                    />
                    Show school logo
                  </label>
                </div>
              </CollapsibleSection>

              <CollapsibleSection title="End Title Card" subtitle="Configure the closing title slide" icon={CreditCard}>
                <div className="space-y-3 mt-3">
                  <label className="flex items-center gap-2 text-sm text-gray-300">
                    <input
                      type="checkbox"
                      checked={endTitleCardSettings?.enabled}
                      onChange={(e) => onEndTitleCardUpdate({ enabled: e.target.checked })}
                    />
                    Add an end title slide
                  </label>
                  <input
                    type="text"
                    value={endTitleCardSettings?.title || ''}
                    onChange={(e) => onEndTitleCardUpdate({ title: e.target.value })}
                    className="w-full p-3 border border-gray-600 bg-gray-700 text-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="End title"
                  />
                  <input
                    type="text"
                    value={endTitleCardSettings?.subtitle || ''}
                    onChange={(e) => onEndTitleCardUpdate({ subtitle: e.target.value })}
                    className="w-full p-3 border border-gray-600 bg-gray-700 text-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="End subtitle"
                  />
                  <div className="flex flex-wrap items-center gap-3">
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={endTitleCardSettings?.backgroundColor || '#1e3a5f'}
                        onChange={(e) => onEndTitleCardUpdate({ backgroundColor: e.target.value })}
                        className="h-10 w-14 border border-gray-600 rounded cursor-pointer"
                      />
                      <span className="text-sm text-gray-400">Background</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={endTitleCardSettings?.textColor || '#ffffff'}
                        onChange={(e) => onEndTitleCardUpdate({ textColor: e.target.value })}
                        className="h-10 w-14 border border-gray-600 rounded cursor-pointer"
                      />
                      <span className="text-sm text-gray-400">Text</span>
                    </div>
                  </div>
                </div>
              </CollapsibleSection>
            </>
          )}

          {activeTab === 'design' && (
            <>
              <CollapsibleSection title="Slide Background" subtitle="Choose colors, gradients, patterns, or images" icon={Palette} defaultOpen>
                <div className="space-y-4 mt-3">
                  {/* Live preview of the pending background on a mini 16:9 slide */}
                  <div className="rounded-xl border border-gray-700 overflow-hidden">
                    <div
                      className="relative w-full"
                      style={{
                        aspectRatio: '16 / 9',
                        ...(getBackgroundStyles
                          ? getBackgroundStyles(typeof pendingBackground === 'string' ? BACKGROUND_PRESETS?.[pendingBackground] : pendingBackground)
                          : {})
                      }}
                    >
                      <div className="absolute top-0 left-0 right-0 h-[12%] flex items-center px-4" style={{ backgroundColor: slideHeaderColor || '#545f7d' }}>
                        <span className="text-white text-xs font-semibold">Slide preview</span>
                      </div>
                      <div className="absolute left-[6%] top-[24%] right-[40%]">
                        <div className="text-gray-800 text-sm font-bold drop-shadow-sm">Sample heading</div>
                        <div className="text-gray-600 text-xs mt-1 drop-shadow-sm">Body text preview on this background</div>
                      </div>
                      <span className="absolute bottom-2 right-3 text-[10px] px-2 py-0.5 rounded-full bg-black/40 text-white">
                        {typeof pendingBackground === 'object' ? (pendingBackground?.name || 'Custom image') : (BACKGROUND_PRESETS?.[pendingBackground]?.name || 'Preset')}
                      </span>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-gray-400 mb-1.5">Apply to</label>
                      <select
                        value={backgroundTarget}
                        onChange={(e) => setBackgroundTarget(e.target.value)}
                        className="w-full p-3 border border-gray-600 bg-gray-700 text-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="all">All Slides</option>
                        {(slides || []).filter((slide) => slide?.slideId).map((slide, index) => (
                          <option key={slide.slideId} value={slide.slideId}>
                            {slide.label || `Slide ${index + 1}`}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-xs font-semibold text-gray-400 mb-1.5">Upload your own image</label>
                      <label className="flex items-center justify-center gap-2 w-full p-3 border-2 border-dashed border-gray-600 hover:border-blue-500 bg-gray-800 text-gray-300 rounded-lg text-sm cursor-pointer transition-colors">
                        <Upload size={16} />
                        <span>Click to choose an image (JPG/PNG, 16:9 works best)</span>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleCustomBackgroundUpload}
                          className="hidden"
                        />
                      </label>
                    </div>
                  </div>

                  {BACKGROUND_PRESETS && (
                    <>
                      <div>
                        <p className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wide">Solid Colors</p>
                        <div className="grid grid-cols-6 gap-2">
                          {Object.entries(BACKGROUND_PRESETS).filter(([_, p]) => p.type === 'color').map(([key, preset]) => (
                            <button
                              key={key}
                              onClick={() => setPendingBackground(key)}
                              className={`h-10 rounded-lg border-2 transition-all ${
                                pendingBackground === key
                                  ? 'border-blue-500 ring-2 ring-blue-200'
                                  : 'border-gray-600 hover:border-gray-500'
                              }`}
                              style={getBackgroundStyles ? getBackgroundStyles(preset) : {}}
                              title={preset.name}
                            />
                          ))}
                        </div>
                      </div>

                      {/* Custom Color Picker */}
                      <div>
                        <p className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wide">Custom Color</p>
                        <div className="flex items-center gap-3">
                          <input
                            type="color"
                            value={customColor}
                            onChange={(e) => setCustomColor(e.target.value)}
                            className="h-10 w-14 border border-gray-600 rounded cursor-pointer"
                          />
                          <input
                            type="text"
                            value={customColor}
                            onChange={(e) => setCustomColor(e.target.value)}
                            className="flex-1 p-2.5 border border-gray-600 bg-gray-700 text-gray-200 rounded-lg text-sm font-mono focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="#ffffff"
                          />
                          <button
                            type="button"
                            onClick={() => setPendingBackground({ type: 'color', value: customColor, name: `Custom (${customColor})` })}
                            className={`px-3 py-2.5 rounded-lg text-xs font-semibold transition-all ${
                              typeof pendingBackground === 'object' && pendingBackground?.type === 'color' && pendingBackground?.value === customColor
                                ? 'bg-blue-600 text-white'
                                : 'bg-gray-700 border border-gray-600 text-gray-300 hover:bg-gray-600'
                            }`}
                          >
                            Use
                          </button>
                        </div>
                      </div>

                      <div>
                        <p className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wide">Gradients</p>
                        <div className="grid grid-cols-6 gap-2">
                          {Object.entries(BACKGROUND_PRESETS).filter(([_, p]) => p.type === 'gradient').map(([key, preset]) => (
                            <button
                              key={key}
                              onClick={() => setPendingBackground(key)}
                              className={`h-10 rounded-lg border-2 transition-all ${
                                pendingBackground === key
                                  ? 'border-blue-500 ring-2 ring-blue-200'
                                  : 'border-gray-600 hover:border-gray-500'
                              }`}
                              style={getBackgroundStyles ? getBackgroundStyles(preset) : {}}
                              title={preset.name}
                            />
                          ))}
                        </div>
                      </div>

                      {/* Custom Gradient Builder */}
                      <div>
                        <p className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wide">Custom Gradient</p>
                        <div className="space-y-2">
                          <div className="flex items-center gap-3">
                            <div className="flex items-center gap-2">
                              <label className="text-xs text-gray-400">From</label>
                              <input
                                type="color"
                                value={customGradientFrom}
                                onChange={(e) => setCustomGradientFrom(e.target.value)}
                                className="h-8 w-10 border border-gray-600 rounded cursor-pointer"
                              />
                            </div>
                            <div className="flex items-center gap-2">
                              <label className="text-xs text-gray-400">To</label>
                              <input
                                type="color"
                                value={customGradientTo}
                                onChange={(e) => setCustomGradientTo(e.target.value)}
                                className="h-8 w-10 border border-gray-600 rounded cursor-pointer"
                              />
                            </div>
                            <div className="flex items-center gap-2">
                              <label className="text-xs text-gray-400">Angle</label>
                              <input
                                type="number"
                                min="0"
                                max="360"
                                value={customGradientAngle}
                                onChange={(e) => setCustomGradientAngle(Number(e.target.value))}
                                className="w-16 p-1.5 border border-gray-600 bg-gray-700 text-gray-200 rounded text-xs focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              />
                            </div>
                            <button
                              type="button"
                              onClick={() => {
                                const gradientValue = `linear-gradient(${customGradientAngle}deg, ${customGradientFrom} 0%, ${customGradientTo} 100%)`;
                                setPendingBackground({ type: 'gradient', value: gradientValue, name: 'Custom Gradient' });
                              }}
                              className={`px-3 py-2 rounded-lg text-xs font-semibold transition-all ${
                                typeof pendingBackground === 'object' && pendingBackground?.type === 'gradient'
                                  ? 'bg-blue-600 text-white'
                                  : 'bg-gray-700 border border-gray-600 text-gray-300 hover:bg-gray-600'
                              }`}
                            >
                              Use
                            </button>
                          </div>
                          <div
                            className="h-8 rounded-lg border border-gray-600"
                            style={{ background: `linear-gradient(${customGradientAngle}deg, ${customGradientFrom} 0%, ${customGradientTo} 100%)` }}
                          />
                        </div>
                      </div>

                      <div>
                        <p className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wide">Patterns</p>
                        <div className="grid grid-cols-6 gap-2">
                          {Object.entries(BACKGROUND_PRESETS).filter(([_, p]) => p.type === 'pattern').map(([key, preset]) => (
                            <button
                              key={key}
                              onClick={() => setPendingBackground(key)}
                              className={`h-10 rounded-lg border-2 transition-all ${
                                pendingBackground === key
                                  ? 'border-blue-500 ring-2 ring-blue-200'
                                  : 'border-gray-600 hover:border-gray-500'
                              }`}
                              style={getBackgroundStyles ? getBackgroundStyles(preset) : {}}
                              title={preset.name}
                            />
                          ))}
                        </div>
                      </div>

                      <div>
                        <p className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wide">Background Images</p>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                          {Object.entries(BACKGROUND_PRESETS).filter(([_, p]) => p.type === 'image').map(([key, preset]) => (
                            <button
                              key={key}
                              onClick={() => setPendingBackground(key)}
                              className={`relative rounded-lg overflow-hidden border-2 transition-all group ${
                                pendingBackground === key
                                  ? 'border-blue-500 ring-2 ring-blue-300'
                                  : 'border-gray-600 hover:border-blue-400'
                              }`}
                              style={{ aspectRatio: '16 / 9' }}
                              title={preset.name}
                            >
                              <img
                                src={preset.preview || preset.value}
                                alt={preset.name}
                                className="absolute inset-0 w-full h-full object-cover"
                                loading="lazy"
                              />
                              <span className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-[11px] py-1 text-center opacity-0 group-hover:opacity-100 transition-opacity">
                                {preset.name}
                              </span>
                              {pendingBackground === key && (
                                <span className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-blue-500 text-white text-[11px] flex items-center justify-center">✓</span>
                              )}
                            </button>
                          ))}
                          {typeof pendingBackground === 'object' && pendingBackground?.type === 'image' && (
                            <div
                              className="relative rounded-lg overflow-hidden border-2 border-blue-500 ring-2 ring-blue-300"
                              style={{ aspectRatio: '16 / 9' }}
                            >
                              <img
                                src={pendingBackground.value}
                                alt={pendingBackground.name || 'Custom image'}
                                className="absolute inset-0 w-full h-full object-cover"
                              />
                              <span className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-[11px] py-1 text-center">
                                {pendingBackground.name || 'Custom image'}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </>
                  )}

                  <div className="flex items-center justify-between gap-3 pt-2">
                    <div className="text-xs text-gray-400">
                      Selected: {typeof pendingBackground === 'object' ? (pendingBackground?.name || 'Custom image') : (BACKGROUND_PRESETS?.[pendingBackground]?.name || 'Preset')}
                    </div>
                    <button
                      type="button"
                      onClick={applySelectedBackground}
                      className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold"
                    >
                      Apply Background
                    </button>
                  </div>
                  {backgroundFeedback && <p className="text-xs text-emerald-400">{backgroundFeedback}</p>}
                </div>
              </CollapsibleSection>

              <CollapsibleSection title="Global Slide CSS" subtitle="Advanced custom CSS for slide styling" icon={Code}>
                <div className="space-y-3 mt-3">
                  <textarea
                    value={slideGlobalCss}
                    onChange={(e) => onSlideGlobalCssChange(e.target.value)}
                    className="w-full h-40 p-3 border border-gray-600 bg-gray-700 text-gray-200 rounded-lg font-mono text-xs focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder=".slide-card { border-radius: 32px; }"
                  />
                  <p className="text-xs text-gray-400">
                    Scope styles to the slide deck (e.g. <code>.slide-deck .slide-card</code>) so they don't affect the rest of the page.
                  </p>
                  {onDownloadScopedCssExample && (
                    <button
                      type="button"
                      onClick={onDownloadScopedCssExample}
                      className="inline-flex items-center gap-2 text-xs font-semibold text-gray-400 hover:text-gray-200"
                    >
                      <Download size={14} />
                      Download scoped CSS example
                    </button>
                  )}
                </div>
              </CollapsibleSection>
            </>
          )}

          {/* =========== EXPORT TAB =========== */}
          {activeTab === 'export' && (
            <>
              <CollapsibleSection title="File Naming" subtitle="Set export file names and version" icon={FileText} defaultOpen>
                <div className="space-y-4 mt-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-400 mb-2">Export File Name</label>
                    <input
                      type="text"
                      value={exportFileName}
                      onChange={(e) => onExportFileNameChange(e.target.value)}
                      placeholder="Week1_Lecture_2025_01_09"
                      className="w-full p-3 border border-gray-600 bg-gray-700 text-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-400 mb-2">HTML Export Title</label>
                    <input
                      type="text"
                      value={htmlExportTitle}
                      onChange={(e) => onHtmlExportTitleChange(e.target.value)}
                      placeholder="Lesson Title for HTML exports"
                      className="w-full p-3 border border-gray-600 bg-gray-700 text-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div className="text-right">
                    <span className="text-sm text-gray-400">Current version: <strong>v{exportVersion}</strong></span>
                  </div>
                </div>
              </CollapsibleSection>

              <CollapsibleSection title="JSON Exports" subtitle="Export lesson data as JSON" icon={Download} defaultOpen>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                  <button onClick={onSave} className="w-full p-3 bg-blue-500 hover:bg-blue-600 text-white rounded-xl font-medium flex items-center justify-center gap-2 transition-colors shadow-sm">
                    <Save size={16} />
                    Full JSON (with images)
                  </button>
                  <button onClick={onExportSlimJson} className="w-full p-3 bg-teal-600 hover:bg-teal-700 text-white rounded-xl font-medium flex items-center justify-center gap-2 transition-colors shadow-sm">
                    <Download size={16} />
                    Slim JSON (no images)
                  </button>
                  {showAdvancedExports && (
                    <button onClick={onExportStableJson} className="w-full p-3 bg-blue-700 hover:bg-blue-800 text-white rounded-xl font-medium flex items-center justify-center gap-2 transition-colors shadow-sm">
                      <Download size={16} />
                      Stable JSON (LLM)
                    </button>
                  )}
                  <button onClick={onExportLlmTemplate} className="w-full p-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-medium flex items-center justify-center gap-2 transition-colors shadow-sm">
                    <Download size={16} />
                    LLM Block Template
                  </button>
                </div>
              </CollapsibleSection>

              <CollapsibleSection title="Presentation Exports" subtitle="Export as HTML, PDF, or PowerPoint" icon={Code}>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                  <button onClick={onExportHTML} className="w-full p-3 bg-purple-500 hover:bg-purple-600 text-white rounded-xl font-medium flex items-center justify-center gap-2 transition-colors shadow-sm">
                    <Download size={16} />
                    Full HTML
                  </button>
                  <button onClick={onExportPathHTML} className="w-full p-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-medium flex items-center justify-center gap-2 transition-colors shadow-sm">
                    <Download size={16} />
                    HTML (Server Paths)
                  </button>
                  <button onClick={onExportSlimHTML} className="w-full p-3 bg-teal-700 hover:bg-teal-800 text-white rounded-xl font-medium flex items-center justify-center gap-2 transition-colors shadow-sm">
                    <Download size={16} />
                    Slim HTML
                  </button>
                  <button onClick={onExportPDF} className="w-full p-3 bg-orange-500 hover:bg-orange-600 text-white rounded-xl font-medium flex items-center justify-center gap-2 transition-colors shadow-sm">
                    <Download size={16} />
                    Export PDF
                  </button>
                  <button onClick={onExportPPTX} className="w-full p-3 bg-rose-600 hover:bg-rose-700 text-white rounded-xl font-medium flex items-center justify-center gap-2 transition-colors shadow-sm">
                    <Download size={16} />
                    PowerPoint (.pptx)
                  </button>
                </div>
              </CollapsibleSection>

              <CollapsibleSection title="Import" subtitle="Load previously saved content" icon={Upload}>
                <div className="space-y-3 mt-3">
                  <input
                    type="file"
                    accept={JSON_ACCEPT}
                    onChange={onLoad}
                    className={HIDDEN_FILE_INPUT_CLASS}
                    id="loadFileSettings"
                  />
                  <button
                    onClick={() => document.getElementById('loadFileSettings').click()}
                    className="w-full p-3 bg-gray-500 hover:bg-gray-600 text-white rounded-xl font-medium flex items-center justify-center gap-2 transition-colors shadow-sm"
                  >
                    <Upload size={16} />
                    Load Content from JSON
                  </button>
                </div>
              </CollapsibleSection>

              <CollapsibleSection title="Advanced" subtitle="Advanced export options" icon={Sliders}>
                <div className="space-y-3 mt-3">
                  <div className="flex items-center justify-between rounded-lg border border-gray-600 bg-gray-700 px-3 py-2">
                    <div>
                      <p className="text-sm font-semibold text-gray-200">Advanced exports</p>
                      <p className="text-xs text-gray-400">Show Stable JSON (LLM) in export menus.</p>
                    </div>
                    <label className="inline-flex items-center gap-2 text-sm font-medium text-gray-200">
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-gray-600 text-blue-600 focus:ring-blue-500"
                        checked={showAdvancedExports}
                        onChange={(e) => setShowAdvancedExports(e.target.checked)}
                      />
                      Enable
                    </label>
                  </div>
                </div>
              </CollapsibleSection>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default TemplateControlsPage;
